import { mapToNewAccessService } from 'app/domain/AccessServiceTypes';
import CONTENTO_TYPES from 'app/domain/ContentoTypes';
import { continueWatchingActiveSelector, continueWatchingItemsSelector } from 'app/reducers/continueWatching';
import { accessVersionChoiceSelector } from 'app/reducers/videoPage';
import { trackTitleData, trackVideoData } from 'app/utils/adform';
import { createContentoHistory } from 'app/utils/continueWatchingUtils';
import { trackCustomEvent } from 'app/utils/statistics/clientStatistics';
import {
    titlePlay,
    titlePlayFromBeginning,
    titlePlayTrailer,
    languageSupportClick,
} from 'app/utils/statistics/customEvents';
import { formatDuration } from 'app/utils/svtDateAndTime';
import { NextRouter, withRouter } from 'next/router';
import React from 'react';
import { connect } from 'react-redux';
import { useQuery } from 'urql';
import { Debug } from '@svt/debug';
import ContinueWatching from 'app/utils/models/ContinueWatching';
import {
    Accessibility,
    DetailsPageQueryQuery,
    DetailsPageQueryQueryVariables,
    SmartStartQueryQuery,
    SmartStartQueryQueryVariables,
} from 'app/graphql/__generated__/types/contento';
import { updateAccessVersionChoice } from '../../actions/videoPage';
import env from '../../env';
import { fontWeightBold } from '../../ui-components/design-tokens';
import PlayImage from '../../ui-components/PlayImage/PlayImage';
import PlayText, { SIZE } from '../../ui-components/typography/PlayText/PlayText';
import { browserMeasurements } from '../../utils/browserMeasurements';
import { createTitleStatistics, createVideoStatistics } from '../../utils/contentStatistics';
import { errorStatistics } from '../../utils/statistics/universalStatistics';
import { contentoImageUrl, createImageProxyUrl, DEFAULT_IMAGE_LARGE } from '../../utils/svtimage';
import { ReactHelmetPlay } from '../ReactHelmet/ReactHelmet';
import SendStatistics from '../CommonElements/SendStatistics';
import NotFoundContainer from '../ErrorHandler/NotFoundContainer';
import ErrorContainer from '../ErrorHandler/ErrorContainer';
import FavoriteButtonHooked from '../FavoriteButton/FavoriteButtonHooked';
import SimilarVideoContentContainer from '../SimilarVideoContent/SimilarVideoContentContainer';
import VariantLinks from '../VariantLinks/VariantLinks';
import OpenModal from '../VideoModal/OpenModal';
import AssociatedContent from './components/AssociatedContent/AssociatedContent';
import CastAndCrew from './components/CastAndCrew/CastAndCrew';
import Categories from './components/Categories/Categories';
import EditorialMessage from './components/EditorialMessage/EditorialMessage';
import Highlights from './components/Highlights/Highlights';
import Metadata from './components/Metadata/Metadata';
import ShowMore from './components/ShowMore/ShowMore';
import {
    Byline,
    HiddenByline,
    NotAvailableInSweden,
    PlaySpan,
    StyledBlurHashCanvas,
    StyledTopArea,
    StyledTopAreaBadge,
    StyledTopAreaHeading,
    StyledTopAreaHighlightText,
    StyledTopAreaLeft,
    StyledTopAreaLeftPlaceholder,
    StyledTopAreaMeta,
    StyledTopAreaPlaceholder,
    StyledTopAreaPlayButton,
    StyledTopAreaPlayButtonArea,
    StyledTopAreaPlayButtonText,
    StyledTopAreaRight,
    StyledTopAreaRightPlaceholder,
    StyledTopAreaSubheading,
    StyledTopAreaUpcomingLive,
} from './components/TopArea/TopArea.styles';
import {
    getAssociatedContentProgress,
    getSmartStartProgress,
    getStructuredData,
    getSubheading,
} from './DetailsPage.helpers';
import {
    ContentWarning,
    Description,
    DetailArea,
    MoreDetails,
    StyledDetailsPage,
    StyledProgressBar,
} from './DetailsPage.styles';
import DETAILS_PAGE_QUERY from './DetailsPageQuery.graphql';
import SMART_START_QUERY from './SmartStartQuery.graphql';

const { error: errorLogger } = Debug('play:detailsPage:error');

const getStatisticsTitle = (
    item: { parent?: { slug: string }; slug: string; svtId: string },
    analyticsIdentifiers: { viewId: string; viewType: string }
) => {
    let statisticsObject;
    if (typeof window !== 'undefined') {
        const browserSize = browserMeasurements(window);

        statisticsObject = createTitleStatistics(
            item.slug,
            item.svtId,
            analyticsIdentifiers.viewId,
            analyticsIdentifiers.viewType,
            browserSize
        );
    }

    return statisticsObject;
};

const getStatisticsVideo = (
    item: { parent?: { slug: string }; slug: string; svtId: string },
    analyticsIdentifiers: { viewId: string; viewType: string }
) => {
    let statisticsObject;
    if (typeof window !== 'undefined') {
        const browserSize = browserMeasurements(window);

        statisticsObject = createVideoStatistics(
            item.parent?.slug,
            item.slug,
            item.svtId,
            analyticsIdentifiers.viewId,
            analyticsIdentifiers.viewType,
            browserSize
        );
    }

    return statisticsObject;
};

export type DetailsPageProps = {
    path: string;
    router: NextRouter;
    updateAccessibilityVersion: () => void;
    continueWatchingIsActive: boolean;
    continueWatching: ContinueWatching;
    currentAccessibilityChoice: Accessibility;
};

const DetailsPage = ({
    path,
    router,
    updateAccessibilityVersion,
    continueWatchingIsActive,
    continueWatching,
    currentAccessibilityChoice,
}) => {
    const [result] = useQuery<DetailsPageQueryQuery, DetailsPageQueryQueryVariables>({
        query: DETAILS_PAGE_QUERY,
        variables: {
            path,
            includeFullOppetArkiv: env.FLAG_ENABLE_ALL_OA,
        },
        requestPolicy: 'cache-first',
    });

    const { data, fetching, error } = result;
    const detailsPage = data?.detailsPageByPath;

    const [smartStartResult] = useQuery<SmartStartQueryQuery, SmartStartQueryQueryVariables>({
        query: SMART_START_QUERY,
        variables: {
            id: detailsPage?.id,
            history: createContentoHistory(continueWatching),
            includeFullOppetArkiv: env.FLAG_ENABLE_ALL_OA,
            accessibility: mapToNewAccessService(currentAccessibilityChoice),
        },
        pause: !detailsPage?.id,
        requestPolicy: 'cache-and-network',
        context: React.useMemo(
            () => ({
                preferGetMethod: false,
            }),
            []
        ),
    });

    const { data: smartStartData, error: smartStartError } = smartStartResult;

    const titleArticleId = detailsPage?.item?.parent?.svtId;

    if (fetching) {
        return (
            <StyledDetailsPage>
                <StyledTopAreaPlaceholder>
                    <StyledTopAreaLeftPlaceholder />
                    <StyledTopAreaRightPlaceholder />
                </StyledTopAreaPlaceholder>
            </StyledDetailsPage>
        );
    }

    if (data) {
        if (!detailsPage) {
            return (
                <>
                    <NotFoundContainer shouldDispatchHttpStatus />
                    <SendStatistics statisticsObject={errorStatistics(404, router.asPath)} />
                </>
            );
        }

        if (!titleArticleId) {
            errorLogger(`${path} has no titleArticleId`);
        }

        const isTitle = 'isTitle' in detailsPage.item;

        const smartStart = smartStartData?.smartStart;

        const url = smartStart?.item.urls?.svtplay || (smartStartError && detailsPage.video?.urls?.svtplay);

        const selectedVideoId = smartStart?.videoSvtId || (smartStartError && detailsPage.video?.svtId);
        const onlyAvailableInSweden =
            smartStart?.item.restrictions.onlyAvailableInSweden ||
            detailsPage.moreDetails.restrictions.onlyAvailableInSweden;

        const label = smartStart?.label || (smartStartError && 'Spela');

        const blurHash = detailsPage.images?.wide?.blurHash;

        const urgentMessage = detailsPage.item?.messages?.urgent;

        const editorialMessage = detailsPage.item?.messages?.editorial;

        const { categories, associatedContent } = detailsPage;

        const badge = smartStart?.badge;

        const accessibleLabel = isTitle
            ? `${smartStart?.label} av ${detailsPage?.item?.parent?.name}`
            : `${smartStart?.label} ${detailsPage.item?.parent?.name ? detailsPage.item.parent.name : ''} `;

        const smartStartProgress = getSmartStartProgress(
            continueWatchingIsActive,
            continueWatching?.toJS(),
            smartStart
        );

        const associatedContentProgress = getAssociatedContentProgress(
            continueWatchingIsActive,
            continueWatching?.toJS(),
            associatedContent
        );

        const positionInUrlFormated =
            router.query.position && formatDuration(Math.floor(router.query.position), true);

        if (isTitle) {
            trackTitleData(detailsPage);
        } else {
            trackVideoData(detailsPage);
        }

        const highlights = 'highlights' in detailsPage.item ? detailsPage.item.highlights : null;

        const highlightDeeplinkItem = highlights?.find(
            (item) => item.positionInSeconds === parseInt(router.query.position, 10)
        );

        const highlightDescription = highlightDeeplinkItem ? ` – ${highlightDeeplinkItem.name}` : '';

        const topImage = highlightDeeplinkItem?.thumbnailId
            ? createImageProxyUrl(
                  String(highlightDeeplinkItem.thumbnailId),
                  0,
                  'large',
                  1024,
                  DEFAULT_IMAGE_LARGE
              )
            : contentoImageUrl(detailsPage.images.wide, 'large', 1024, DEFAULT_IMAGE_LARGE);

        const isSingle = detailsPage?.item?.parent?.__typename === 'Single';
        const canonical = env.BASE_URL + (detailsPage?.item?.urls?.svtplay || '');
        const ogCanonical = highlightDeeplinkItem?.positionInSeconds
            ? `${canonical}?position=${highlightDeeplinkItem?.positionInSeconds}`
            : canonical;
        const metaName =
            isTitle || isSingle || !detailsPage.item.parent
                ? `${detailsPage.item.name}${highlightDescription}`
                : `${detailsPage.item.parent.name} – ${detailsPage.item.name}${highlightDescription}`;

        const clips = associatedContent.find((a) => a.selectionType === 'clips');
        const firstTrailer = clips?.items.find((a) => a.item.__typename === 'Trailer');

        const trailerVideoId = firstTrailer && firstTrailer?.item.videoSvtId;
        const trailerUrl = firstTrailer && firstTrailer?.item.urls.svtplay;

        const frontMoreThanTrailer = trailerVideoId !== selectedVideoId;

        const isLive = smartStart?.item?.live?.liveNow;

        const shouldShowTrailer =
            !([CONTENTO_TYPES.CLIP, CONTENTO_TYPES.TRAILER] as Array<String>).includes(
                detailsPage?.item.__typename
            ) &&
            trailerUrl &&
            frontMoreThanTrailer &&
            !isLive;

        const handleVariantLinkClick = (accessibility) => {
            updateAccessibilityVersion(accessibility);
            trackCustomEvent(languageSupportClick(accessibility, true));
        };

        return (
            <StyledDetailsPage itemScope>
                <ReactHelmetPlay
                    pathForFeeds={router.asPath}
                    title={metaName}
                    description={detailsPage.description}
                    image={topImage}
                >
                    <meta
                        property="og:type"
                        key="og:type"
                        content={isTitle ? 'video.tv_show' : 'video.episode'}
                    />
                    <meta property="og:image" key="og:image" content={topImage} />
                    <meta name="twitter:image:src" key="twitter:image:src" content={topImage} />
                    <meta property="og:url" key="og:url" content={ogCanonical} />
                    <meta name="twitter:url" key="twitter:url" content={ogCanonical} />
                    <link rel="canonical" key="canonical" href={canonical} />
                    <script type="application/ld+json">
                        {getStructuredData(detailsPage, topImage, url, isLive)}
                    </script>
                </ReactHelmetPlay>
                <StyledTopArea>
                    {blurHash && <StyledBlurHashCanvas hash={blurHash} width={256} height={144} punch={1} />}
                    <StyledTopAreaLeft>
                        <PlayImage alt="" src={topImage} />
                        {smartStartProgress ? (
                            <StyledProgressBar progress={smartStartProgress} selected />
                        ) : null}
                    </StyledTopAreaLeft>
                    <StyledTopAreaRight>
                        <StyledTopAreaMeta>
                            {badge && (
                                <StyledTopAreaBadge
                                    text={badge.text}
                                    theme={badge.type}
                                    accessibilityText={badge.altText}
                                    overlay={false}
                                />
                            )}
                            {isSingle && (
                                <Byline aria-hidden="true">{detailsPage.item.shortDescription}</Byline>
                            )}
                            <StyledTopAreaHeading>
                                {detailsPage.item?.parent?.name
                                    ? detailsPage.item.parent.name
                                    : detailsPage.item.name}
                            </StyledTopAreaHeading>
                            <StyledTopAreaSubheading data-rt="video-player-splash-play">
                                {getSubheading(detailsPage.item, isTitle)}
                            </StyledTopAreaSubheading>

                            {highlightDeeplinkItem && (
                                <StyledTopAreaHighlightText>
                                    {`${positionInUrlFormated}: ${highlightDeeplinkItem.name}`}
                                </StyledTopAreaHighlightText>
                            )}
                            {isSingle && <HiddenByline>{detailsPage.item.shortDescription}</HiddenByline>}

                            <StyledTopAreaPlayButtonArea
                                onlyAvailableInSweden={onlyAvailableInSweden}
                                playFromPosition={!!positionInUrlFormated}
                            >
                                {url && (
                                    <OpenModal
                                        videoId={selectedVideoId}
                                        videoUrl={url}
                                        position={router.query.position}
                                        extraQuery={
                                            highlightDeeplinkItem && {
                                                highlight: highlightDeeplinkItem?.slug,
                                            }
                                        }
                                    >
                                        {(onClick) => {
                                            const isSmartStart = smartStart?.videoSvtId === selectedVideoId;

                                            const handlePlay = () => {
                                                trackCustomEvent(titlePlay(selectedVideoId, isSmartStart));
                                                onClick();
                                            };

                                            return (
                                                <StyledTopAreaPlayButton
                                                    role="button"
                                                    data-rt="top-area-play-button"
                                                    onlyAvailableInSweden={onlyAvailableInSweden}
                                                    playFromPosition={!!positionInUrlFormated}
                                                    onClick={handlePlay}
                                                >
                                                    <StyledTopAreaPlayButtonText aria-label={accessibleLabel}>
                                                        {positionInUrlFormated
                                                            ? `Spela från ${positionInUrlFormated}`
                                                            : label}
                                                    </StyledTopAreaPlayButtonText>
                                                </StyledTopAreaPlayButton>
                                            );
                                        }}
                                    </OpenModal>
                                )}
                                {'validFromFormatted' in detailsPage.item && (
                                    <>
                                        {detailsPage.isUpcomingLive && !url && (
                                            <StyledTopAreaUpcomingLive>
                                                Sändningen startar{' '}
                                                {detailsPage.item.validFromFormatted.toLowerCase()}
                                            </StyledTopAreaUpcomingLive>
                                        )}
                                        {detailsPage.isUpcomingVOD && (
                                            <PlayText size={SIZE.XLARGE} weight={fontWeightBold}>
                                                Sänds {detailsPage.item.validFromFormatted.toLowerCase()}
                                            </PlayText>
                                        )}
                                    </>
                                )}

                                {shouldShowTrailer && (
                                    <OpenModal videoId={trailerVideoId} videoUrl={trailerUrl}>
                                        {(onClick) => {
                                            const handlePlayTrailer = () => {
                                                trackCustomEvent(titlePlayTrailer(trailerVideoId));
                                                onClick();
                                            };

                                            return (
                                                <StyledTopAreaPlayButton
                                                    role="button"
                                                    data-rt="top-area-trailer-button"
                                                    onlyAvailableInSweden={
                                                        detailsPage.moreDetails.restrictions
                                                            .onlyAvailableInSweden
                                                    }
                                                    secondaryButton
                                                    playFromPosition={!!positionInUrlFormated}
                                                    onClick={handlePlayTrailer}
                                                >
                                                    <StyledTopAreaPlayButtonText aria-label="Spela trailer för programmet">
                                                        Spela trailer
                                                    </StyledTopAreaPlayButtonText>
                                                </StyledTopAreaPlayButton>
                                            );
                                        }}
                                    </OpenModal>
                                )}
                                {isLive && (
                                    <OpenModal videoId={selectedVideoId} videoUrl={url} position={1}>
                                        {(onClick) => {
                                            const handlePlayFromStart = () => {
                                                trackCustomEvent(titlePlayFromBeginning(selectedVideoId));
                                                onClick();
                                            };

                                            return (
                                                <StyledTopAreaPlayButton
                                                    role="button"
                                                    data-rt="top-area-play-button"
                                                    onlyAvailableInSweden={onlyAvailableInSweden}
                                                    secondaryButton
                                                    previousIcon
                                                    playFromPosition={!!positionInUrlFormated}
                                                    onClick={handlePlayFromStart}
                                                >
                                                    <StyledTopAreaPlayButtonText aria-label="Spela direktsändning från början">
                                                        <PlaySpan>Spela </PlaySpan> från början
                                                    </StyledTopAreaPlayButtonText>
                                                </StyledTopAreaPlayButton>
                                            );
                                        }}
                                    </OpenModal>
                                )}
                            </StyledTopAreaPlayButtonArea>
                            <NotAvailableInSweden
                                weight={fontWeightBold}
                                onlyAvailableInSweden={
                                    detailsPage.moreDetails.restrictions.onlyAvailableInSweden
                                }
                            >
                                Kan bara ses i Sverige
                            </NotAvailableInSweden>

                            <VariantLinks
                                videos={smartStart?.item.videos}
                                onClick={handleVariantLinkClick}
                                selectedVideoId={selectedVideoId}
                                context="top-area"
                                onlyAvailableInSweden={
                                    detailsPage.moreDetails.restrictions.onlyAvailableInSweden
                                }
                            />
                        </StyledTopAreaMeta>
                    </StyledTopAreaRight>
                </StyledTopArea>
                <MoreDetails>
                    <DetailArea position="left">
                        {urgentMessage && (
                            <EditorialMessage
                                message={urgentMessage.message}
                                linkText={urgentMessage.linkText}
                                link={urgentMessage.link}
                                urgent
                                boldText
                            />
                        )}
                        <Description size={SIZE.LARGE}>{detailsPage.description}</Description>
                        {detailsPage?.contentWarning && (
                            <ContentWarning size={SIZE.LARGE} weight={fontWeightBold}>
                                {detailsPage.contentWarning.text}
                            </ContentWarning>
                        )}

                        <FavoriteButtonHooked titleId={titleArticleId} />

                        {highlights && (
                            <Highlights videoId={selectedVideoId} videoUrl={url} highlights={highlights} />
                        )}
                        {categories && <Categories categories={categories} />}
                        {editorialMessage && (
                            <EditorialMessage
                                message={editorialMessage.message}
                                linkText={editorialMessage.linkText}
                                link={editorialMessage.link}
                            />
                        )}
                    </DetailArea>
                    <DetailArea position="right">
                        <Metadata
                            validFrom={detailsPage.moreDetails?.validFrom}
                            validTo={detailsPage.moreDetails?.validTo}
                            productionYear={detailsPage?.moreDetails?.productionYearRange}
                            originalProgramTitle={
                                'originalProgramTitle' in detailsPage.item
                                    ? detailsPage.item.originalProgramTitle
                                    : ''
                            }
                            isUpcomingVOD={detailsPage.isUpcomingVOD}
                            onlyAvailableInSwedenFormatted={
                                detailsPage.moreDetails?.restrictions?.onlyAvailableInSwedenFormatted
                            }
                            nextEpisodeAvailableFormatted={
                                'nextEpisodeAvailableFormatted' in detailsPage.item
                                    ? detailsPage.item.nextEpisodeAvailableFormatted
                                    : undefined
                            }
                        />
                    </DetailArea>
                    {!isTitle && detailsPage.castAndCrew?.length > 0 && (
                        <DetailArea position="left">
                            <ShowMore allByDefault={isTitle || isSingle}>
                                <CastAndCrew castAndCrew={detailsPage.castAndCrew} />
                            </ShowMore>
                        </DetailArea>
                    )}
                </MoreDetails>

                {!isSingle && (
                    <AssociatedContent
                        selections={associatedContent}
                        selectedVideoId={selectedVideoId}
                        progress={associatedContentProgress}
                    />
                )}

                <SimilarVideoContentContainer id={detailsPage.item.parent.id} />
                {isTitle ? (
                    <SendStatistics
                        key={path}
                        statisticsObject={getStatisticsTitle(
                            detailsPage.item,
                            detailsPage.analyticsIdentifiers
                        )}
                    />
                ) : (
                    <SendStatistics
                        key={path}
                        statisticsObject={getStatisticsVideo(
                            detailsPage.item,
                            detailsPage.analyticsIdentifiers
                        )}
                    />
                )}
            </StyledDetailsPage>
        );
    }

    if (error) {
        return (
            <>
                <ErrorContainer pathname={router.asPath} shouldDispatchHttpStatus />
                <SendStatistics statisticsObject={errorStatistics(500, router.asPath)} />
            </>
        );
    }

    return null;
};

class DetailsPageContainer extends React.Component<DetailsPageProps> {
    shouldComponentUpdate(nextProps) {
        return nextProps.openModal === false;
    }

    render() {
        const {
            path,
            router,
            updateAccessibilityVersion,
            continueWatchingIsActive,
            continueWatching,
            currentAccessibilityChoice,
        } = this.props;

        return (
            <DetailsPage
                path={path}
                router={router}
                updateAccessibilityVersion={updateAccessibilityVersion}
                continueWatchingIsActive={continueWatchingIsActive}
                continueWatching={continueWatching}
                currentAccessibilityChoice={currentAccessibilityChoice}
            />
        );
    }
}

const mapDispatchToProps = (dispatch) => ({
    updateAccessibilityVersion: (variant) => dispatch(updateAccessVersionChoice(variant)),
});

const mapStateToProps = (state, ownProps) => ({
    ...ownProps,
    continueWatchingIsActive: continueWatchingActiveSelector(state),
    continueWatching: continueWatchingItemsSelector(state),
    currentAccessibilityChoice: accessVersionChoiceSelector(state),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DetailsPageContainer));
